import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { 
    Button, 
    Input, 
    Select, 
    Table, 
    Modal, 
    message, 
    Tag, 
    Space, 
    Descriptions, 
    Divider, 
    Tooltip,
    Row,
    Col,
    Card,
    Badge,
    Spin
} from 'antd';
import { 
    SearchOutlined, 
    ReloadOutlined, 
    UserOutlined,
    TrophyOutlined,
    BookOutlined,
    RiseOutlined,
    FileTextOutlined,
    SafetyCertificateOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { ResumeManagementPageModel } from '../pageModels/ResumeManagementPageModel';
import { ResumeDetailModal } from '../../components/ResumeDetail';
import { 
    Resume, 
    ResumeFilter, 
    EducationLevel, 
    SchoolType, 
    CompanyType,
    getEducationText,
    getGenderText,
    getSchoolTypeText,
    getCompanyTypeText,
    calculateAge,
    calculateWorkYears
} from '../../models/Resume';
import { globalStore } from '../../models/globalStore';

const { Option } = Select;

const model = new ResumeManagementPageModel();

const ResumeManagement: React.FC = () => {
    const [filterForm, setFilterForm] = useState<ResumeFilter>({});
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        globalStore.selectedMenuKey = 'resume-management';
        model.loadResumes();
    }, []);

    const handleSearch = async () => {
        setSearching(true);
        try {
            model.setFilter({ ...filterForm });
            await model.loadResumes();
        } finally {
            setSearching(false);
        }
    };

    const handleReset = () => {
        setFilterForm({});
        model.resetFilter();
        model.loadResumes();
    };

    const handleRowClick = (record: Resume) => {
        model.loadResumeDetail(record.id);
    };

    const handleUpdateServiceStatus = async (id: string, isServing: boolean) => {
        try {
            await model.updateResumeStatus(id, isServing);
            message.success(`已${isServing ? '标记为正在服务' : '标记为未在服务'}`);
        } catch (error) {
            message.error('更新服务状态失败');
        }
    };

    const columns = [
        {
            title: '姓名',
            key: 'name',
            width: 120,
            render: (_: any, record: Resume) => (
                <Space>
                    {record.isServing && (
                        <Badge 
                            status="success" 
                            text="" 
                            style={{ marginRight: 0, transform: 'scale(1.5)' }}
                        />
                    )}
                    <span style={{ fontWeight: 'bold' }}>{record.name}</span>
                </Space>
            )
        },
        {
            title: '性别',
            key: 'gender',
            width: 80,
            render: (_: any, record: Resume) => getGenderText(record.gender)
        },
        {
            title: '工作年限',
            key: 'workYears',
            width: 100,
            render: (_: any, record: Resume) => `${calculateWorkYears(record.firstWorkDate)}年`
        },
        {
            title: '最高学历',
            key: 'education',
            width: 120,
            render: (_: any, record: Resume) => getEducationText(record.education)
        },
        {
            title: '最佳学校类型',
            key: 'bestSchoolType',
            width: 140,
            render: (_: any, record: Resume) => record.bestSchoolName || getSchoolTypeText(record.bestSchoolType)
        },
        {
            title: '最佳公司类型',
            key: 'bestCompanyType',
            width: 140,
            render: (_: any, record: Resume) => record.bestCompanyName || getCompanyTypeText(record.bestCompanyType)
        },
        {
            title: '期望薪资',
            key: 'expectedSalary',
            width: 120,
            render: (_: any, record: Resume) => `${record.expectedSalaryStart}-${record.expectedSalaryEnd}K`
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_: any, record: Resume) => (
                <Button 
                    type={record.isServing ? "default" : "primary"}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateServiceStatus(record.id, !record.isServing);
                    }}
                >
                    {record.isServing ? "标记为未在服务" : "标记为正在服务"}
                </Button>
            )
        }
    ];

    return (
        <div className="resume-management-container" style={{ padding: '24px' }}>
            {/* 筛选区 */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '16px', paddingInline: '16px' }}>
                    <Input
                        placeholder="卖家ID/手机号"
                        value={filterForm.sellerIdOrPhone}
                        onChange={(e) => setFilterForm({ ...filterForm, sellerIdOrPhone: e.target.value || undefined })}
                        prefix={<SearchOutlined />}
                        onPressEnter={handleSearch}
                        style={{ width: '200px', borderRadius: 0 }}
                    />
                    <Input
                        placeholder="卖家昵称/简历内容关键词"
                        value={filterForm.sellerNicknameOrKeyword}
                        onChange={(e) => setFilterForm({ ...filterForm, sellerNicknameOrKeyword: e.target.value || undefined })}
                        prefix={<SearchOutlined />}
                        onPressEnter={handleSearch}
                        style={{ width: '200px', borderRadius: 0 }}
                    />
                    <Select
                        placeholder="公司类型"
                        allowClear
                        style={{ width: '200px' }}
                        value={filterForm.bestCompanyType}
                        onChange={(value) => setFilterForm({ ...filterForm, bestCompanyType: value })}
                    >
                        <Option value={CompanyType.SMALL_MICRO}>小微企业</Option>
                        <Option value={CompanyType.MEDIUM}>中型企业</Option>
                        <Option value={CompanyType.SECOND_TIER_INTERNET}>二线互联网企业</Option>
                        <Option value={CompanyType.FIRST_TIER_INTERNET}>一线互联网</Option>
                        <Option value={CompanyType.BBAT}>BBAT</Option>
                    </Select>
                    <Input
                        placeholder="参考薪资(K)"
                        type="number"
                        style={{ width: '200px', borderRadius: 0 }}
                        value={filterForm.expectedSalary}
                        onChange={(e) => setFilterForm({ ...filterForm, expectedSalary: Number(e.target.value) || undefined })}
                    />
                    <Input
                        placeholder="最小工作年限"
                        type="number"
                        style={{ width: '200px', borderRadius: 0 }}
                        value={filterForm.minWorkYears}
                        onChange={(e) => setFilterForm({ ...filterForm, minWorkYears: Number(e.target.value) || undefined })}
                    />
                    <Input
                        placeholder="最大工作年限"
                        type="number"
                        style={{ width: '200px', borderRadius: 0 }}
                        value={filterForm.maxWorkYears}
                        onChange={(e) => setFilterForm({ ...filterForm, maxWorkYears: Number(e.target.value) || undefined })}
                    />
                    <Input
                        placeholder="最小年龄"
                        type="number"
                        style={{ width: '200px', borderRadius: 0 }}
                        value={filterForm.minAge}
                        onChange={(e) => setFilterForm({ ...filterForm, minAge: Number(e.target.value) || undefined })}
                    />
                    <Input
                        placeholder="最大年龄"
                        type="number"
                        style={{ width: '200px', borderRadius: 0 }}
                        value={filterForm.maxAge}
                        onChange={(e) => setFilterForm({ ...filterForm, maxAge: Number(e.target.value) || undefined })}
                    />
                    <Select
                        placeholder="排序字段"
                        allowClear
                        style={{ width: '200px' }}
                        value={filterForm.sortBy}
                        onChange={(value) => setFilterForm({ ...filterForm, sortBy: value })}
                    >
                        <Option value="createdAt">创建时间</Option>
                        <Option value="servedMonthCount">服务月数</Option>
                        <Option value="age">年龄</Option>
                    </Select>
                    <Select
                        placeholder="排序方向"
                        allowClear
                        style={{ width: '200px' }}
                        value={filterForm.sortOrder}
                        onChange={(value) => setFilterForm({ ...filterForm, sortOrder: value })}
                    >
                        <Option value="asc">升序</Option>
                        <Option value="desc">降序</Option>
                    </Select>
                    <Select
                        placeholder="服务状态"
                        allowClear
                        style={{ width: '200px' }}
                        value={filterForm.isServing}
                        onChange={(value) => setFilterForm({ ...filterForm, isServing: value })}
                    >
                        <Option value={true}>正在服务</Option>
                        <Option value={false}>未在服务</Option>
                    </Select>
                    <Button style={{ width: '200px' }} type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                        查询
                    </Button>
                    <Button style={{ width: '200px' }} onClick={handleReset} icon={<ReloadOutlined />}>
                        重置
                    </Button>
                </div> 
            </div>

            {/* 列表展示区 */}
            <div className="list-section">
                <Table
                    columns={columns}
                    dataSource={model.resumes}
                    rowKey="id"
                    loading={model.loading}
                    pagination={false}
                    scroll={{ x: 1200 }}
                    onRow={(record) => ({
                        onClick: () => handleRowClick(record),
                        style: { cursor: 'pointer' }
                    })}
                />
            </div>

            {/* 简历详情弹框 */}
            <Modal
                title="简历详情"
                open={model.detailModalVisible}
                onCancel={() => model.setDetailModalVisible(false)}
                footer={null}
                width={500}
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: '90vh', overflow: 'hidden' }}
                destroyOnClose
            >
                {model.selectedResume && (
                    <ResumeDetailModal resume={model.selectedResume} />
                )}
            </Modal>

            {/* 查询中提示弹框 */}
            <Modal
                title="查询中"
                open={searching}
                footer={null}
                closable={false}
                maskClosable={false}
                width={300}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    </div>
                    <div>正在查询简历数据，请稍候...</div>
                </div>
            </Modal>
        </div>
    );
};

export default observer(ResumeManagement);